import React from "react";
import { ArrowRight, Phone } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import useReveal from "../../hooks/useReveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1707190205120-228c6656086c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMHBvb2wlMjBkdXNrfGVufDB8fHx8MTc3NzgxMTkwMnww&ixlib=rb-4.1.0&q=85";

const TELEGRAM_URL = "https://t.me/nmn_energy";

const Hero = () => {
  const { t } = useLanguage();
  const revealRef = useReveal();

  return (
    <section
      id="top"
      data-testid="hero-section"
      ref={revealRef}
      className="relative min-h-screen w-full overflow-hidden bg-black grain"
    >
      {/* Background image with ken-burns */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Luxury villa at dusk"
          className="w-full h-full object-cover ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.85)_85%)]"></div>
      </div>

      {/* Vertical guides */}
      <div className="hidden md:block absolute inset-y-0 left-12 w-px bg-white/5"></div>
      <div className="hidden md:block absolute inset-y-0 right-12 w-px bg-white/5"></div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 pt-40 pb-24 md:pt-48 md:pb-32 min-h-screen flex flex-col justify-between">
        <div className="max-w-4xl">
          <p
            className="reveal font-mono-l text-[11px] text-[#e8d4a8] mb-8 flex items-center gap-4"
            data-testid="hero-eyebrow"
          >
            <span className="w-8 h-px bg-[#e8d4a8]"></span>
            {t.hero.eyebrow}
          </p>

          <h1
            className="reveal reveal-delay-1 font-display text-white text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-balance"
            data-testid="hero-title"
          >
            {t.hero.title}
            <br />
            <span className="italic text-[#e8d4a8]">{t.hero.titleAccent}</span>
          </h1>

          <p
            className="reveal reveal-delay-2 mt-8 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed font-light"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div
            className="reveal reveal-delay-3 mt-10 flex flex-col sm:flex-row gap-4"
            data-testid="hero-cta-group"
          >
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#e8d4a8] text-black text-[12px] tracking-[0.2em] uppercase font-medium hover:bg-white transition-colors duration-300"
              data-testid="hero-primary-cta"
            >
              {t.hero.primaryCta}
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
            <a
              href="#locations"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#e8d4a8]/50 text-[#e8d4a8] text-[12px] tracking-[0.2em] uppercase hover:bg-[#e8d4a8]/10 transition-all duration-300"
              data-testid="hero-secondary-cta"
            >
              {t.hero.secondaryCta}
            </a>
          </div>
        </div>

        {/* Trust pillars (no fake numbers) */}
        <div
          className="reveal reveal-delay-4 mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-12 max-w-5xl pt-10 border-t border-white/10"
          data-testid="hero-pillars"
        >
          {t.hero.pillars.map((p, i) => (
            <div
              key={i}
              className="flex flex-col"
              data-testid={`hero-pillar-${i}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="font-mono-l text-[10px] text-[#e8d4a8]">
                  0{i + 1}
                </span>
                <span className="w-6 h-px bg-[#e8d4a8]/60"></span>
              </div>
              <span className="font-display text-lg md:text-xl text-white leading-snug">
                {p.k}
              </span>
              <span className="mt-2 text-white/55 text-sm leading-relaxed font-light">
                {p.v}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-white/40">
        <span className="font-mono-l text-[9px]">{t.hero.scroll}</span>
        <span className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent"></span>
      </div>
    </section>
  );
};

export default Hero;
